import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import {
  Edit2,
  FileText,
  History,
  RefreshCw,
  RotateCcw,
  Save,
} from 'lucide-react'
import {
  type CaseScope,
  type CaseScopeVersion,
} from '../../../types/case-container'

export function ScopeLoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('scope.loadingScope')}</p>
      </div>
    </div>
  )
}

export function ScopeErrorState({
  error,
  onRetry,
  t,
}: {
  error: string
  onRetry: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

interface ScopeHeaderProps {
  scope: CaseScope | null
  showHistory: boolean
  onToggleHistory: () => void
  onRefetch: () => void
  t: TFunction<'cases'>
}

export function ScopeHeader({
  scope,
  showHistory,
  onToggleHistory,
  onRefetch,
  t,
}: ScopeHeaderProps): ReactElement {
  const date = scope?.updatedAt
    ? new Date(scope.updatedAt).toLocaleDateString()
    : t('scope.lastUpdatedNever')
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
          <FileText className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('scope.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('scope.versionInfo', { version: scope?.currentVersion ?? 1, date })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
            showHistory
              ? 'border-[#4e5e82] bg-[#4e5e82]/5 text-lime-700 dark:bg-[#8a9bba]/10 dark:text-lime-400'
              : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
          }`}
        >
          <History className="h-4 w-4" />
          {t('scope.history')}
        </button>
        <button
          onClick={onRefetch}
          className="rounded-lg border border-slate-300 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          title={t('actions.refresh')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface VersionHistoryProps {
  versions: CaseScopeVersion[]
  loadingVersions: boolean
  currentVersion: number | undefined
  t: TFunction<'cases'>
}

export function VersionHistoryPanel({
  versions,
  loadingVersions,
  currentVersion,
  t,
}: VersionHistoryProps): ReactElement {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-medium text-slate-900 dark:text-slate-100">
          {t('scope.versionHistory')}
        </h3>
        {loadingVersions && <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {versions.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('scope.noVersionHistory')}</p>
      ) : (
        <div className="max-h-48 space-y-2 overflow-y-auto">
          {versions.map((v) => (
            <VersionRow key={v.id} version={v} currentVersion={currentVersion} t={t} />
          ))}
        </div>
      )}
    </div>
  )
}

function VersionRow({
  version,
  currentVersion,
  t,
}: {
  version: CaseScopeVersion
  currentVersion: number | undefined
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 py-2 last:border-0 dark:border-slate-700">
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {t('scope.version', { version: version.version })}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {version.changeSummary ?? t('scope.noSummary')} |{' '}
          {new Date(version.createdAt).toLocaleString()}
        </p>
      </div>
      {version.version !== currentVersion && (
        <button
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-lime-600 dark:text-slate-400 dark:hover:text-lime-400"
          title={t('scope.restoreVersion')}
        >
          <RotateCcw className="h-3 w-3" />
          {t('scope.restore')}
        </button>
      )}
    </div>
  )
}

interface ScopeDocumentProps {
  scope: CaseScope | null
  isEditing: boolean
  editContent: string
  setEditContent: (v: string) => void
  changeSummary: string
  setChangeSummary: (v: string) => void
  saving: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  t: TFunction<'cases'>
}

export function ScopeDocument(props: ScopeDocumentProps): ReactElement {
  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <ScopeDocumentHeader {...props} />
      <ScopeDocumentBody {...props} />
    </div>
  )
}

function ScopeDocumentHeader({
  isEditing,
  saving,
  onEdit,
  onCancel,
  onSave,
  t,
}: ScopeDocumentProps): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
      <h3 className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
        <FileText className="h-4 w-4" />
        {t('scope.scopeDocument')}
      </h3>
      {isEditing ? (
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#4e5e82] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            {t('actions.save')}
          </button>
        </div>
      ) : (
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <Edit2 className="h-3.5 w-3.5" />
          {t('actions.edit')}
        </button>
      )}
    </div>
  )
}

function ScopeDocumentBody({
  scope,
  isEditing,
  editContent,
  setEditContent,
  changeSummary,
  setChangeSummary,
  t,
}: ScopeDocumentProps): ReactElement {
  if (isEditing) {
    return (
      <div className="space-y-4 p-4">
        <textarea
          value={editContent}
          onChange={(e) => {
            setEditContent(e.target.value)
          }}
          className="h-64 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          placeholder={t('scope.scopePlaceholder')}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
            {t('scope.changeSummaryLabel')}
          </label>
          <input
            type="text"
            value={changeSummary}
            onChange={(e) => {
              setChangeSummary(e.target.value)
            }}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            placeholder={t('scope.changeSummaryPlaceholder')}
          />
        </div>
      </div>
    )
  }
  return (
    <div className="p-4">
      <div className="prose prose-slate dark:prose-invert min-h-[200px] max-w-none">
        {scope?.content ? (
          <pre className="font-sans whitespace-pre-wrap text-slate-700 dark:text-slate-300">
            {scope.content}
          </pre>
        ) : (
          <p className="text-slate-400 italic dark:text-slate-500">{t('scope.noScopeContent')}</p>
        )}
      </div>
    </div>
  )
}

export { NoteForm, NotesSection } from './case-scope-tab.notes'
